import * as SheetPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { CancelIcon } from '../icons';
import { cn } from '../utils';

import { buttonVariants } from './Button';
import {
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollThumb,
  VerticalScrollbar,
} from './scroll-area';
import { Separator } from './Separator';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[1200] bg-black/50',
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  id: string;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
          side === 'right' &&
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm',
          side === 'left' &&
            'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm',
          side === 'top' &&
            'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b',
          side === 'bottom' &&
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t',
          'z-[1300] border-border',
          className,
        )}
        {...props}
      >
        {/* First in DOM so Radix auto-focuses Close on open, not an inner
            element (e.g. a tooltip trigger that would auto-open) */}
        <SheetPrimitive.Close
          className={cn(
            buttonVariants({ variant: 'ghost', size: 'sm' }),
            'absolute top-4 right-4 aspect-square',
          )}
        >
          <CancelIcon />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn('flex flex-col gap-1.5 p-4', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <>
      <SheetDivider className={cn('mt-auto')} />
      <div
        data-slot="sheet-footer"
        className={cn('flex flex-col gap-2 p-4', className)}
        {...props}
      />
    </>
  );
}

function SheetBody({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <ScrollAreaRoot
      type="scroll"
      scrollHideDelay={1000}
      className="flex flex-col flex-1 min-h-0"
    >
      <ScrollAreaViewport className="w-full h-full no-scrollbar">
        <div
          data-slot="sheet-body"
          className={cn('p-4 flex flex-col', className)}
          {...props}
        >
          {children}
        </div>
      </ScrollAreaViewport>
      <VerticalScrollbar>
        <ScrollThumb />
      </VerticalScrollbar>
    </ScrollAreaRoot>
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        'text-foreground text-lg h-10 flex items-center',
        className,
      )}
      {...props}
    />
  );
}
function SheetDivider({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return <Separator className={className} {...props} />;
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetDivider,
};
